// Lazy-loaded route components for better initial load performance
const Layout = () => import('@/pages/layout/Layout.vue')
const Explorer = () => import('@/pages/explorer/Explorer.vue')
const Dashboard = () => import('@/pages/dashboard/Dashboard.vue')
const ChartDetailsViewer = () => import('@/pages/ChartDetailsViewer/ChartDetailsViewer.vue')
const CustomMetricsPage = () => import('@/pages/CustomMetrics/CustomMetricsPage.vue')
const CustomMetricsListPage = () => import('@/pages/CustomMetrics/CustomMetricsListPage.vue')
const ProjectList = () => import('@/pages/ProjectList/ProjectList.vue')
const MembersPage = () => import('@/pages/members/MembersPage.vue')
const HelpPage = () => import('@/pages/help/HelpPage.vue')
const OidcCallbackView = () => import('@/pages/auth/OidcCallbackView.vue')
const AcceptOrgInvitation = () => import('@/pages/auth/AcceptOrgInvitation.vue')
const ProjectConfiguration = () => import('@/pages/project/ProjectConfiguration.vue')
const SnapshotManagment = () => import('@/pages/snapshot/SnapshotManagment.vue')
const GlobalFilters = () => import('@/pages/filters/GlobalFilters.vue')
const FeatureFlagPage = () => import('@/pages/debug/FeatureFlagPage.vue')

import CONSTANTS from '@/constants/Config.constants'
import labels from '@/translations/labels.json'
import { RouteLocationNormalized, RouteLocationRaw, RouteRecordRaw } from 'vue-router'
import { useOrganizations } from '@/stores/organizations'
import { OrganizationsService, ResourcesService } from '@itemis-solutions/platform-client-sdk'
import { useProjectStore } from '@/stores/projectStore'
import { useEnvStore } from '@/stores/envStore'
import axios from 'axios'
import { Project } from '@/models'
import { useNotificationStore } from '@/stores/notificationStore'

async function resolveProject(projectId: string): Promise<Project | undefined> {
  const projectIdNum = Number(projectId)
  if (!Number.isFinite(projectIdNum)) return undefined

  // Check the already-loaded project store first to avoid an extra round-trip.
  const projectStore = useProjectStore()
  let project = projectStore.getProjectById(projectIdNum)

  if (!project) {
    try {
      const envStore = useEnvStore()
      const apiUrl = envStore.variables.ANALYZE_API_BACKEND_URL
      const response = await axios.get(`${apiUrl}/project/${projectIdNum}`)
      project = response.data?.data
    } catch {
      return undefined
    }
  }
  return project
}
/**
 * Resolves the organization slug for the project identified by `projectId`.
 * First checks the already-loaded store, then falls back to a backend fetch.
 * Uses the project's `resourceId` and the platform ResourcesService to look up
 * the owning organization.  Returns `undefined` when the org cannot be determined.
 */
async function resolveOrgSlugForProject(project: Project): Promise<string | undefined> {
  if (!project.resourceId) return undefined

  try {
    const { data: resource } = await ResourcesService.getResource({
      resourceId: project.resourceId,
    })
    const { data: org } = await OrganizationsService.getOrganization({
      organizationIdOrSlug: resource.organization_id,
    })
    return org.slug
  } catch {
    return undefined
  }
}

/**
 * Navigation guard applied to legacy /project/:projectId/… routes.
 * When a projectId is present, resolves the organization from the project's
 * resource instead of using the default organization.  Falls back to the
 * current/default organization if the project's org cannot be determined.
 * If no organization can be resolved at all the navigation continues to the
 * legacy route as a fallback.
 */
async function resolveOrgAndRedirect(
  to: RouteLocationNormalized,
): Promise<RouteLocationRaw | void> {
  const orgStore = useOrganizations()

  let orgSlug: string | undefined

  // When a projectId is in the URL, prefer the org that owns the project.
  const projectId = to.params.projectId
  if (projectId) {
    const project = await resolveProject(String(projectId))
    if (!project) {
      useNotificationStore().showError(`Project ${projectId} for this URL not found`)
      return { name: CONSTANTS.ROUTES_NAME.PROJECTLIST }
    }
    orgSlug = await resolveOrgSlugForProject(project)
  }

  // Fall back to the currently selected / default organization.
  if (!orgSlug) {
    orgSlug = orgStore.currentOrganization?.slug
  }

  if (!orgSlug && !projectId) {
    try {
      const { data } = await OrganizationsService.getDefaultOrganization({})
      orgSlug = data.slug
    } catch {
      return // Cannot determine org → fall through to legacy route
    }
  }

  if (orgSlug) {
    const newPath = to.fullPath
      .replace(/^\/project\//, `/organization/${orgSlug}/project/`)
      .replace(/^\/projectlist$/, `/organization/${orgSlug}/projectlist`)
    if (newPath !== to.fullPath) {
      return { path: newPath, replace: true }
    }
  }
}

export const globalRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: { name: CONSTANTS.ROUTES_NAME.PROJECTLIST },
    meta: { requiresAuth: true },
  },
  {
    path: '/callback',
    name: CONSTANTS.ROUTES_NAME.CALLBACK,
    component: OidcCallbackView,
    meta: { requiresAuth: false },
  },
  {
    path: CONSTANTS.BASE_ROUTES.FEATURE_FLAGS,
    name: 'FeatureFlags',
    component: FeatureFlagPage,
    meta: { requiresAuth: false },
  },
  {
    path: '/accept-invitation/:invitation_id',
    name: 'accept-invitation',
    component: AcceptOrgInvitation,
    meta: { requiresAuth: true },
  },
  {
    path: '/app',
    name: 'Layout',
    component: Layout,
    children: [
      // ─── Organization area ────────────────────────────────────────────────
      {
        // /organization  →  OrganizationsList (pick an organization)
        path: '/organization',
        name: CONSTANTS.ROUTES_NAME.ORGANIZATION_LIST,
        component: async () =>
          (await import('@itemis-solutions/platform-web-components')).OrganizationsView,
        props: () => ({}),
        meta: { title: 'Organization List', requiresAuth: true },
      },
      {
        // /organization/:orgSlug  →  org-scoped ProjectList
        // Named so the OrganizationsList platform component can navigate here.
        path: '/organization/:orgSlug',
        name: 'platform-web-components.OrganizationsList:showOrganization',
        component: ProjectList,
        meta: { title: 'Project List', requiresAuth: true },
      },
      {
        // /organization/:orgSlug/members  →  org-scoped MembersPage
        path: '/organization/:orgSlug/members',
        name: 'platform-web-components.OrganizationsList:showMembers',
        component: MembersPage,
        meta: { title: 'Members', requiresAuth: true },
      },
      {
        path: '/organization/:orgSlug/projectlist',
        name: labels.SIDEBAR.MAIN.PROJECT_LIST,
        component: ProjectList,
        meta: {
          title: 'Project List',
          requiresAuth: true,
        },
      },

      // ─── Org-scoped project sub-routes ────────────────────────────────────
      {
        path: '/organization/:orgSlug/project/:projectId',
        redirect: (to) => ({
          name: CONSTANTS.ROUTES_NAME.ORG_PROJECT_CONFIGURATION,
          params: to.params,
          meta: { title: 'Project Configuration', requiresAuth: true },
        }),
      },
      {
        path: '/organization/:orgSlug/project/:projectId/snapshot/:snapshotId/dashboard/:dashboardId?',
        name: CONSTANTS.ROUTES_NAME.ORG_DASHBOARD,
        component: Dashboard,
        meta: { title: 'Dashboard', requiresAuth: true },
      },
      {
        path: '/organization/:orgSlug/project/:projectId/snapshot/:snapshotId/explorer',
        name: CONSTANTS.ROUTES_NAME.ORG_EXPLORER,
        component: Explorer,
        meta: { title: 'Explorer', requiresAuth: true },
        children: [
          {
            path: 'artifact/:artifactId',
            name: CONSTANTS.ROUTES_NAME.ORG_EXPLORER_GRAPH,
            component: Explorer,
          },
        ],
      },
      {
        path: '/organization/:orgSlug/project/:projectId/configuration',
        name: CONSTANTS.ROUTES_NAME.ORG_PROJECT_CONFIGURATION,
        component: ProjectConfiguration,
        meta: { title: 'Project Configuration', requiresAuth: true },
      },
      {
        path: '/organization/:orgSlug/project/:projectId/snapshots',
        name: CONSTANTS.ROUTES_NAME.ORG_SNAPSHOT_MANAGMENT,
        component: SnapshotManagment,
        meta: { title: 'Snapshot Management', requiresAuth: true },
      },
      {
        path: '/organization/:orgSlug/project/:projectId/filters',
        name: CONSTANTS.ROUTES_NAME.ORG_GLOBAL_FILTERS,
        component: GlobalFilters,
        meta: { title: 'Global Filters', requiresAuth: true },
      },
      {
        path: '/organization/:orgSlug/project/:projectId/metrics',
        name: CONSTANTS.ROUTES_NAME.ORG_CUSTOM_METRICS,
        component: CustomMetricsListPage,
        meta: { title: 'Custom Metrics', requiresAuth: true },
      },
      {
        path: '/organization/:orgSlug/project/:projectId/metrics/:customMetricId',
        name: CONSTANTS.ROUTES_NAME.ORG_CUSTOM_METRIC,
        component: CustomMetricsPage,
        meta: { title: 'Custom Metric', requiresAuth: true },
      },
      {
        path: '/organization/:orgSlug/project/:projectId/snapshot/:snapshotId/dashboard/:metricsDetailsPath/series/:seriesName/totalCount/:totalServerItemsLength',
        name: CONSTANTS.ROUTES_NAME.ORG_DASHBOARD_DETAILS_VIEWER,
        component: ChartDetailsViewer,
        meta: { title: 'Dashboard Details Viewer', requiresAuth: true },
      },

      // ─── Legacy /project/:projectId/… routes ─────────────────────────────
      // Each has a beforeEnter guard that resolves the current organization and
      // redirects to the equivalent /organization/:orgSlug/project/… URL.
      // The component is kept as a fallback if the org cannot be determined.
      {
        path: '/project/:projectId',
        beforeEnter: resolveOrgAndRedirect,
        redirect: (to) => ({
          name: CONSTANTS.ROUTES_NAME.ORG_PROJECT_CONFIGURATION,
          // orgSlug will be undefined here if guard didn't redirect; the guard
          // handles the redirect so this redirect is only a safety net.
          params: to.params,
          meta: { title: 'Project Configuration', requiresAuth: true },
        }),
      },
      {
        path: '/project/:projectId/configuration',
        name: labels.SIDEBAR.MAIN.PROJECT_CONFIGURATION,
        component: ProjectConfiguration,
        beforeEnter: resolveOrgAndRedirect,
        meta: { title: 'Project Configuration', requiresAuth: true },
      },
      {
        path: '/project/:projectId/snapshots',
        name: labels.SIDEBAR.MAIN.SNAPSHOTS_MANAGMENT,
        component: SnapshotManagment,
        beforeEnter: resolveOrgAndRedirect,
        meta: { title: 'Snapshot Management', requiresAuth: true },
      },
      {
        path: '/project/:projectId/filters',
        name: labels.SIDEBAR.MAIN.GLOBAL_FILTERS,
        component: GlobalFilters,
        beforeEnter: resolveOrgAndRedirect,
        meta: { title: 'Global Filters', requiresAuth: true },
      },
      {
        path: '/project/:projectId/snapshot/:snapshotId/dashboard/:dashboardId?',
        name: labels.SIDEBAR.MAIN.DASHBOARD,
        component: Dashboard,
        beforeEnter: resolveOrgAndRedirect,
        meta: { title: 'Dashboard', requiresAuth: true },
      },
      {
        path: '/project/:projectId/snapshot/:snapshotId/explorer',
        name: labels.SIDEBAR.MAIN.EXPLORER,
        component: Explorer,
        beforeEnter: resolveOrgAndRedirect,
        meta: { title: 'Explorer', requiresAuth: true },
        children: [
          {
            name: CONSTANTS.ROUTES_NAME.EXPLORER_GRAPH,
            path: 'artifact/:artifactId',
            component: Explorer,
          },
        ],
      },
      {
        path: '/project/:projectId/metrics',
        name: labels.CUSTOM_METRIC.CUSTOM_METRICS,
        component: CustomMetricsListPage,
        beforeEnter: resolveOrgAndRedirect,
        meta: { title: 'Custom Metrics', requiresAuth: true },
      },
      {
        path: '/project/:projectId/metrics/:customMetricId',
        name: labels.CUSTOM_METRIC.CUSTOM_METRIC,
        component: CustomMetricsPage,
        beforeEnter: resolveOrgAndRedirect,
        meta: { title: 'Custom Metric', requiresAuth: true },
      },
      {
        path: '/project/:projectId/snapshot/:snapshotId/dashboard/:metricsDetailsPath/series/:seriesName/totalCount/:totalServerItemsLength',
        name: labels.DASHBOARD_DETAILS_VIEWER.TITLE,
        component: ChartDetailsViewer,
        beforeEnter: resolveOrgAndRedirect,
        meta: { title: 'Dashboard Details Viewer', requiresAuth: true },
      },

      // ─── Legacy flat routes (kept for backward compatibility) ─────────────
      {
        // Redirect old project list to the organization overview
        path: '/projectlist',
        name: CONSTANTS.ROUTES_NAME.PROJECTLIST,
        component: ProjectList,
        beforeEnter: resolveOrgAndRedirect,
        meta: { title: 'Project List', requiresAuth: true },
      },
      {
        // Redirect old members page to the organization overview so the user
        // can pick the right org (we have no org context here).
        path: '/members',
        name: labels.SIDEBAR.MAIN.MEMBERS,
        redirect: { name: CONSTANTS.ROUTES_NAME.ORGANIZATION_LIST },
      },
      {
        path: '/help',
        name: labels.HELP.TITLE,
        component: HelpPage,
        meta: { title: 'Help', requiresAuth: true },
      },
    ],
  },
]
